<template>
  <Modal ref="modalRef" width="40rem">
    <template #body>
      <View gap="xl" direction="column">
        <View gap="md" direction="column" align="center">
          <Text variant="h2">Disconnect CodeBox?</Text>
          <Text variant="body-large" color="secondary" align="center">
            {{ assignedCount }} of your properties currently open with a CodeBox lockbox.
          </Text>
        </View>

        <WhatToExpectCard :items="whatToExpectNextItems" />
      </View>
    </template>

    <template #footer>
      <View gap="md" :grow="1" class="actions">
        <Button variant="secondary" @click="close">Cancel</Button>
        <Button
          variant="primary"
          @click="disconnect"
          :loading="isDisconnecting"
          data-testid="codebox-disconnect-confirm"
        >
          Disconnect
        </Button>
      </View>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { useQueryCache } from '@pinia/colada';
import { computed, ref } from 'vue';

import { Button, Modal, Text, View, WhatToExpectCard, WhatToExpectItem, useAlertsStore } from '@tallo/design-system';
import {
  codeboxService,
  getCodeboxIntegrationQueryKey,
  getCodeboxesQueryKey,
  useCodeboxesQuery,
} from '@tallo/investor/property';

const modalRef = ref<InstanceType<typeof Modal>>();
const alertsStore = useAlertsStore();
const isDisconnecting = ref(false);

const codeboxes = useCodeboxesQuery(() => true);
const assignedCount = computed(() => (codeboxes.data.value ?? []).filter((codebox) => codebox.property).length);

const whatToExpectNextItems: WhatToExpectItem[] = [
  {
    icon: 'x-close-circle',
    title: 'No more door codes',
    description: 'Renters who confirm a self-guided tour will stop receiving a code for these properties.',
  },
  {
    icon: 'x-close-circle',
    title: 'Lockboxes are released',
    description: 'Every lockbox is removed from its property, and those listings lose their access method.',
  },
  {
    icon: 'x-close-circle',
    title: 'Credentials are deleted',
    description: 'Reconnecting later means entering your CodeBox username and password again.',
  },
];

async function disconnect() {
  try {
    isDisconnecting.value = true;
    await codeboxService.disconnect();

    const queryCache = useQueryCache();
    await queryCache.invalidateQueries({ key: getCodeboxIntegrationQueryKey() });
    await queryCache.invalidateQueries({ key: getCodeboxesQueryKey() });

    alertsStore.success('CodeBox disconnected');
    close();
  } catch (error) {
    console.error('Failed to disconnect CodeBox:', error);
    alertsStore.error('Failed to disconnect CodeBox');
  } finally {
    isDisconnecting.value = false;
  }
}

function open() {
  modalRef.value?.show();
}

function close() {
  modalRef.value?.close();
}

defineExpose({ open, close, modalRef });
</script>

<style scoped lang="scss">
.actions > * {
  width: 100%;
}
</style>
